import React from 'react';
import { isNaN } from 'lodash';
import Script from 'next/script';

import { isServer } from '@tager/web-core';
import { Page } from '@tager/web-components';
import { getSettingValueByKey } from '@tager/web-modules';

import { CustomApp_PageContext } from '@/typings/hocs';
import NotFoundPage from '@/pages/404';
import {
  getCarBlogThunk,
  getDetailedCarThunk,
  getSimilarCarsThunk,
  resetDetailedCarState,
} from '@/store/reducers/detailed-car';
import Layout from '@/layout';
import {
  fetchCarDetailedBannerThunk,
  fetchStockCarsBannerThunk,
} from '@/store/reducers/banners';
import DetailedCarNew from '@/modules/DetailedCar/DetailedCarNew';
import CarsStock from '@/modules/CarsStock';
import {
  getStockBrandsAndModelsThunk,
  getStockCarListThunk,
  getStockFilterParamsThunk,
  resetCarsStockState,
  setCurrentSort,
} from '@/store/reducers/cars-stock';
import {
  convertedSelectedFilters,
  createBrandsAndModelsPayload,
  getSelectedCarsByUrl,
  getSelectedFilterFromUrl,
  getSEOType,
  handleSetSelectedFiltersFromUrl,
  isWrongPagePath,
  setActiveBrandsAndModelsFromSettings,
} from '@/modules/CarsStock/components/common.helpers';
import DetailedCarNewPdfTemplate from '@/modules/DetailedCar/DetailedCarNewPdfTemplate';
import { sortingData } from '@/modules/CarsStock/components/common.constants';
import { fetchSeoTemplateThunk } from '@/store/reducers/seo-template';
import { useCarsStockSeo } from '@/modules/CarsStock/components/common.hooks';
import { useDetailedCarSeo } from '@/modules/DetailedCar/DetailedCar.hooks';
import { redirect } from '@/utils/common';
import { getSimilarCarsDataParams } from '@/modules/DetailedCar/DetailedCar.helper';

type Props = {
  pageType: 'CARS_STOCK_PAGE' | 'DETAILED_CAR_PAGE' | 'NOT_FOUND';
};

function NewCarsPage(props: Props) {
  const carsStockSeo = useCarsStockSeo();

  const detailedCarSeo = useDetailedCarSeo('new');

  if (props.pageType === 'NOT_FOUND') {
    return <NotFoundPage />;
  }

  if (props.pageType === 'DETAILED_CAR_PAGE') {
    return (
      <Page
        title={detailedCarSeo.title}
        description={detailedCarSeo.description}
        openGraphImage={detailedCarSeo.openGraphImage}
      >
        <Layout isPrint>
          <DetailedCarNew />
        </Layout>
        <noindex>
          <DetailedCarNewPdfTemplate />
        </noindex>

        <Script
          id="detailedCarBreadcrumb"
          type="application/ld+json"
          strategy="beforeInteractive"
          dangerouslySetInnerHTML={{
            __html: detailedCarSeo.breadcrumbMicromarking,
          }}
        />

        <Script
          id="detailedCarProduct"
          type="application/ld+json"
          strategy="beforeInteractive"
          dangerouslySetInnerHTML={{
            __html: detailedCarSeo.productMicromarking,
          }}
        />

        {detailedCarSeo.eventMicromarking.map((micromarking, index) => (
          <Script
            key={index}
            id="detailedCarEvent"
            type="application/ld+json"
            strategy="beforeInteractive"
            dangerouslySetInnerHTML={{
              __html: micromarking,
            }}
          />
        ))}
      </Page>
    );
  }

  return (
    <Page
      title={carsStockSeo.title}
      description={carsStockSeo.description}
      openGraphImage={carsStockSeo.openGraphImage}
      canonicalUrl={carsStockSeo.canonicalUrl}
    >
      <Layout>
        <CarsStock carsType="new" />
      </Layout>

      <Script
        id="stockCarsProduct"
        type="application/ld+json"
        strategy="beforeInteractive"
        dangerouslySetInnerHTML={{
          __html: carsStockSeo.productMicromarking,
        }}
      />

      {carsStockSeo.eventMicromarking.map((micromarking, index) => (
        <Script
          key={index}
          id="stockCarsEvent"
          type="application/ld+json"
          strategy="beforeInteractive"
          dangerouslySetInnerHTML={{
            __html: micromarking,
          }}
        />
      ))}
    </Page>
  );
}

NewCarsPage.getInitialProps = async ({
  store,
  res,
  query,
}: CustomApp_PageContext) => {
  const state = store.getState();

  const isEnabledCarsStock = getSettingValueByKey(
    state.tager.settings.settingItemList.data,
    'STOCK_NEW_ENABLED'
  );

  if (!isEnabledCarsStock) {
    if (res) {
      res.statusCode = 404;
    }

    return {
      pageType: 'NOT_FOUND',
    };
  }

  const slugAsNumber = Number(query.slug);
  const isDetailedCarRequest = !isNaN(slugAsNumber);

  const currentSort =
    getSettingValueByKey(
      state.tager.settings.settingItemList.data,
      'STOCK_NEW_INITIAL_SORT'
    ) ?? '';

  const seoNoIndexDetailedCar = getSettingValueByKey(
    state.tager.settings.settingItemList.data,
    'DETAILED_CAR_NOINDEX'
  );

  const seoNoIndexStockFilterPages = getSettingValueByKey(
    state.tager.settings.settingItemList.data,
    'STOCK_NOINDEX_FILTER_PAGES'
  );

  const activeBrands =
    getSettingValueByKey(
      state.tager.settings.settingItemList.data,
      'STOCK_NEW_BRANDS'
    ) ?? [];

  const activeModels =
    getSettingValueByKey(
      state.tager.settings.settingItemList.data,
      'STOCK_NEW_MODELS'
    ) ?? [];

  const allSortOptions =
    getSettingValueByKey(
      state.tager.settings.settingItemList.data,
      'STOCK_NEW_SORT_LIST'
    ) ?? [];

  if (isDetailedCarRequest) {
    store.dispatch(resetDetailedCarState());

    try {
      if (isServer()) {
        if (res && seoNoIndexDetailedCar) {
          res.setHeader('X-Robots-Tag', 'noindex');
        }

        const detailedCarData = await store.dispatch(
          getDetailedCarThunk(slugAsNumber)
        );

        if (activeBrands && activeBrands.length !== 0) {
          if (
            !activeBrands.includes(String(detailedCarData.catalog.brand.id)) ||
            !activeModels.includes(String(detailedCarData.catalog.model?.id))
          ) {
            if (res) {
              res.statusCode = 404;
            }

            return {
              pageType: 'NOT_FOUND',
            };
          }
        }

        await Promise.all([
          store.dispatch(
            getSimilarCarsThunk(
              'new',
              String(slugAsNumber),
              getSimilarCarsDataParams(detailedCarData)
            )
          ),
          store.dispatch(
            fetchCarDetailedBannerThunk({
              type: 'new',
              brand: String(detailedCarData.catalog.brand.id),
              model: String(detailedCarData.catalog.model?.id),
            })
          ),
          store.dispatch(getCarBlogThunk('new', String(slugAsNumber))),
          store.dispatch(fetchSeoTemplateThunk('stock_new_detailed')),
        ]);
      }

      return {
        pageType: 'DETAILED_CAR_PAGE',
      };
    } catch (error: any) {
      const reason = error.body?.reason;

      if (reason === 'DELETED') {
        redirect('/cars/new', undefined, res, 301);
      }

      if (error.status.code === 404) {
        if (res) {
          res.statusCode = 404;
        }

        return { pageType: 'NOT_FOUND' };
      }

      return {
        pageType: 'DETAILED_CAR_PAGE',
      };
    }
  } else {
    if (query?.slug && query.slug.length > 3) {
      if (res) {
        res.statusCode = 404;
      }

      redirect('/cars/new', undefined, res);
    }

    store.dispatch(resetCarsStockState());
    store.dispatch(
      setCurrentSort(
        sortingData.find((item) => item.value === currentSort) ??
          sortingData.filter((item) => allSortOptions.includes(item.value))[0]
      )
    );

    try {
      if (isServer()) {
        const selectedItems = getSelectedFilterFromUrl(query.slug);

        if (
          res &&
          seoNoIndexStockFilterPages &&
          Object.keys(selectedItems).length > 0
        ) {
          res.setHeader('X-Robots-Tag', 'noindex');
        }

        const brandsAndModelsResponse = await store.dispatch(
          getStockBrandsAndModelsThunk('new')
        );

        setActiveBrandsAndModelsFromSettings(
          store.dispatch,
          activeBrands,
          activeModels,
          brandsAndModelsResponse
        );

        if (query.slug) {
          if (isWrongPagePath(brandsAndModelsResponse, selectedItems)) {
            if (res) {
              res.statusCode = 404;
            }

            redirect('/cars/new', undefined, res);
          }

          // На дилерских сайтах редирект с ссылок /cars/new/:brand на /cars/new при отключенных брендах
          if (activeBrands && activeBrands.length !== 0) {
            if (selectedItems.brand && selectedItems.brand.length === 1) {
              let selectedBrandId = null;

              brandsAndModelsResponse.forEach((brand) => {
                if (
                  selectedItems.brand &&
                  brand.alias === selectedItems.brand[0]
                ) {
                  selectedBrandId = brand.id;
                }
              });

              if (
                selectedBrandId &&
                !activeBrands.includes(String(selectedBrandId))
              ) {
                redirect('/cars/new', undefined, res, 301);
              }
            }
          }
        }

        const filtersParamsResponse = await store.dispatch(
          getStockFilterParamsThunk(
            'new',
            createBrandsAndModelsPayload(
              getSelectedCarsByUrl(selectedItems, brandsAndModelsResponse),
              activeBrands,
              activeModels
            )
          )
        );

        const convertedFilters = convertedSelectedFilters(
          selectedItems,
          brandsAndModelsResponse,
          filtersParamsResponse
        );

        if (query.slug) {
          handleSetSelectedFiltersFromUrl(store.dispatch, convertedFilters);
        }

        let bannerThunk = null;

        if (
          convertedFilters.brand.length === 0 &&
          convertedFilters.model.length === 0 &&
          convertedFilters.page === 1
        ) {
          bannerThunk = fetchStockCarsBannerThunk({
            type: 'new',
          });
        } else if (
          convertedFilters.brand.length === 1 &&
          convertedFilters.model.length === 1 &&
          convertedFilters.page === 1
        ) {
          const { brand, model } = convertedFilters;

          bannerThunk = fetchStockCarsBannerThunk({
            type: 'new',
            brand: brand[0].value,
            model: model[0].value,
          });
        } else if (
          convertedFilters.brand.length === 1 &&
          convertedFilters.model.length === 0 &&
          convertedFilters.page === 1
        ) {
          const { brand } = convertedFilters;

          bannerThunk = fetchStockCarsBannerThunk({
            type: 'new',
            brand: brand[0].value,
          });
        }

        const seoType = getSEOType('new', selectedItems);

        await Promise.all([
          store.dispatch(getStockCarListThunk('new')),
          store.dispatch(fetchSeoTemplateThunk(seoType)),
          bannerThunk && store.dispatch(bannerThunk),
        ]);
      }

      return {
        pageType: 'CARS_STOCK_PAGE',
      };
    } catch (error: any) {
      if (error.status.code === 404) {
        if (res) {
          res.statusCode = 404;
        }

        redirect('/cars/new', undefined, res);
      }

      return {
        pageType: 'CARS_STOCK_PAGE',
      };
    }
  }
};

export default NewCarsPage;
